import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { supabase } from '../config/supabase';
import logger from '../utils/logger';
import {
  DEVELOPER_EMAIL,
  ROLES,
  PERMISSIONS,
  DEFAULT_ROLE_PERMISSIONS,
  PAGE_PERMISSIONS,
  ALL_PAGES,
  DEFAULT_ROLE_PAGES,
  isDeveloperEmail,
  canModifyRole,
  ROLE_HIERARCHY
} from '../config/rbac';

const RBACContext = createContext();

// Supabase table name for user roles
const USERS_ROLES_TABLE = 'users_roles';

export function useRBAC() {
  const context = useContext(RBACContext);
  if (!context) {
    throw new Error('useRBAC must be used within an RBACProvider');
  }
  return context;
}

export function RBACProvider({ children }) {
  const { currentUser, logout } = useAuth();
  // Default to null during loading - user must be verified
  const [userRole, setUserRole] = useState(null);
  const [userPermissions, setUserPermissions] = useState([]);
  const [assignedCampaigns, setAssignedCampaigns] = useState([]);
  const [allowedPages, setAllowedPages] = useState([]);
  const [customPermissions, setCustomPermissions] = useState(null);
  const [loading, setLoading] = useState(true);
  const [allUsers, setAllUsers] = useState([]);
  const [tableExists, setTableExists] = useState(true);
  const [accessDenied, setAccessDenied] = useState(false); // Block unregistered users
  const [accessDeniedReason, setAccessDeniedReason] = useState(''); // Reason for denial

  // ============================================
  // IMPERSONATION STATE (View as User feature)
  // ============================================
  const [isImpersonating, setIsImpersonating] = useState(false);
  const [impersonatedUser, setImpersonatedUser] = useState(null);
  const [originalUserData, setOriginalUserData] = useState(null);
  // Dynamic signal-page access: true when the user has at least one
  // profile_users membership, so non-admin users who are assigned to
  // a signal profile get the Signals nav item automatically.
  const [hasSignalProfileAccess, setHasSignalProfileAccess] = useState(false);

  // Fetch user role from database or determine by email
  const fetchUserRole = useCallback(async () => {
    if (!currentUser?.email) {
      setUserRole(null);
      setUserPermissions([]);
      setAssignedCampaigns([]);
      setAllowedPages([]);
      setHasSignalProfileAccess(false);
      setLoading(false);
      return;
    }

    try {
      // Check signal profile membership (non-blocking, best-effort)
      supabase.rpc('get_user_profiles', { p_user_email: currentUser.email.toLowerCase() })
        .then(({ data }) => {
          setHasSignalProfileAccess(Array.isArray(data) && data.length > 0);
        })
        .catch(() => { /* ignore — worst case the nav item stays hidden */ });

      // CRITICAL: Developer email check comes first (validated via Supabase Auth email)
      // This is the permanent developer - always full access
      if (isDeveloperEmail(currentUser.email)) {
        setUserRole(ROLES.DEVELOPER);
        setUserPermissions(Object.values(PERMISSIONS));
        setAssignedCampaigns([]); // Empty means all campaigns for developer
        setAllowedPages(ALL_PAGES.map(p => p.key)); // All pages for developer
        setLoading(false);
        return;
      }

      // Try to fetch role from database for non-developer users
      // If table doesn't exist, gracefully default to admin access
      const { data, error } = await supabase
        .from(USERS_ROLES_TABLE)
        .select('*')
        .eq('email', currentUser.email.toLowerCase())
        .single();

      // Handle table not existing (42P01) or other errors
      if (error) {
        if (error.code === '42P01' || error.message?.includes('does not exist')) {
          // Table doesn't exist yet - default to ADMIN for all users until setup
          logger.warn('RBAC table not found. Run RBAC-SCHEMA.sql in Supabase. Defaulting to admin access.');
          setTableExists(false);
          setUserRole(ROLES.ADMIN);
          setUserPermissions(Object.values(PERMISSIONS));
          setAssignedCampaigns([]);
          setAllowedPages(ALL_PAGES.map(p => p.key));
          setLoading(false);
          return;
        }
        
        if (error.code !== 'PGRST116') {
          // PGRST116 = not found, which is fine for new users
          logger.error('Error fetching user role:', error);
        }
      }

      if (data) {
        // CHECK: User must be active
        if (data.is_active === false) {
          logger.warn('Access denied: User account is deactivated');
          setAccessDenied(true);
          setAccessDeniedReason('Your account has been deactivated. Please contact an administrator.');
          return;
        }
        
        // CHECK: Moderators and Users must have at least one campaign assigned
        if ((data.role === ROLES.MODERATOR || data.role === ROLES.USER) && 
            (!data.assigned_campaigns || data.assigned_campaigns.length === 0)) {
          logger.warn(`Access denied: ${data.role} has no campaigns assigned`);
          setAccessDenied(true);
          setAccessDeniedReason('You have no campaigns assigned. Please contact an administrator to assign campaigns.');
          return;
        }
        
        // CHECK: Moderators and Users must have at least one page access
        const userPages = data.allowed_pages && data.allowed_pages.length > 0 
          ? data.allowed_pages 
          : DEFAULT_ROLE_PAGES[data.role] || [];
        if ((data.role === ROLES.MODERATOR || data.role === ROLES.USER) && userPages.length === 0) {
          logger.warn(`Access denied: ${data.role} has no pages assigned`);
          setAccessDenied(true);
          setAccessDeniedReason('You have no page access assigned. Please contact an administrator.');
          return;
        }
        
        // ACCESS GRANTED - Set user data
        setAccessDenied(false);
        setUserRole(data.role);
        // Use custom permissions if set, otherwise use default for role
        const perms = data.custom_permissions || DEFAULT_ROLE_PERMISSIONS[data.role] || [];
        setUserPermissions(perms);
        setAssignedCampaigns(data.assigned_campaigns || []);
        setAllowedPages(userPages);
        setCustomPermissions(data.custom_permissions);
      } else {
        // User NOT in database - BLOCK ACCESS
        logger.warn('Access denied: Email not registered in the system');
        setAccessDenied(true);
        setAccessDeniedReason('Your email is not registered in the system. Please contact an administrator to get access.');
        // Don't logout here - let AccessDeniedScreen handle it to avoid race condition
      }
    } catch (error) {
      logger.error('Error in fetchUserRole:', error);
      // SECURITY: Default to most restrictive access on error
      // Do NOT grant admin access on failure - this prevents privilege escalation
      setAccessDenied(true);
      setAccessDeniedReason('Unable to verify your access permissions. Please try again or contact an administrator.');
      setUserRole(null);
      setUserPermissions([]);
      setAssignedCampaigns([]);
      setAllowedPages([]);
    } finally {
      setLoading(false);
    }
  }, [currentUser?.email]);

  // Load user role when auth changes
  useEffect(() => {
    fetchUserRole();
  }, [fetchUserRole]);

  // ============================================
  // PERMISSION CHECKING FUNCTIONS
  // ============================================

  /**
   * Check if user has a specific permission
   */
  const hasPermission = useCallback((permission) => {
    if (!permission) return true;
    
    // Developer always has all permissions
    if (userRole === ROLES.DEVELOPER) return true;
    
    return userPermissions.includes(permission);
  }, [userRole, userPermissions]);

  /**
   * Check if user can access a specific page
   * Uses allowedPages for moderators/users, full access for admin/developer
   */
  const canAccessPage = useCallback((pageKey) => {
    if (!pageKey) return true;
    
    // During loading, allow all pages to prevent UI flicker
    if (loading) return true;
    
    // Developer always has access to everything
    if (userRole === ROLES.DEVELOPER) return true;
    
    // Admin has access to everything
    if (userRole === ROLES.ADMIN) return true;
    
    // Admin panel is special - only for admin/developer
    if (pageKey === 'admin') {
      return false; // Moderators and users cannot access admin
    }
    
    // For moderators and users, check allowedPages
    if (!allowedPages || allowedPages.length === 0) return false;
    
    // Users with signal profile membership get signals page access
    // even if their allowed_pages doesn't explicitly include it
    if (pageKey === 'signals' && hasSignalProfileAccess) return true;
    
    return allowedPages.includes(pageKey);
  }, [userRole, allowedPages, loading, hasSignalProfileAccess]);

  /**
   * Check if user can access a specific campaign's data
   */
  const canAccessCampaign = useCallback((campaignName) => {
    if (!campaignName) return true;
    
    // Developer and Admin have access to all campaigns
    if (userRole === ROLES.DEVELOPER || userRole === ROLES.ADMIN) return true;
    
    // Empty assignedCampaigns means NO access (not all access) for non-admin users
    if (!assignedCampaigns || assignedCampaigns.length === 0) return false;
    
    return assignedCampaigns.includes(campaignName);
  }, [userRole, assignedCampaigns]);

  /**
   * Filter data array by assigned campaigns
   */
  const filterByCampaign = useCallback((data, campaignField = 'campaign_name') => {
    if (!Array.isArray(data)) return data;
    
    // Developer and Admin see all data
    if (userRole === ROLES.DEVELOPER || userRole === ROLES.ADMIN) return data;
    
    // No assigned campaigns = no data for moderators
    if (!assignedCampaigns || assignedCampaigns.length === 0) return [];
    
    return data.filter(item => {
      const itemCampaign = item[campaignField];
      return itemCampaign && assignedCampaigns.includes(itemCampaign);
    });
  }, [userRole, assignedCampaigns]);

  /**
   * Check if current user is developer
   */
  const isDeveloper = useCallback(() => {
    return userRole === ROLES.DEVELOPER;
  }, [userRole]);

  /**
   * Check if current user is admin or higher
   * Returns true during loading to prevent UI from breaking
   */
  const isAdminOrHigher = useCallback(() => {
    // SECURITY: During loading or if role not set, default to false
    // This prevents brief privilege escalation during auth check
    if (loading || !userRole) return false;
    return userRole === ROLES.DEVELOPER || userRole === ROLES.ADMIN;
  }, [userRole, loading]);

  /**
   * Check if current user can delete leads/campaigns
   * Developers and Admins always have delete access
   * Moderators/Users need explicit CAN_DELETE permission
   */
  const canDelete = useCallback(() => {
    // Developer and Admin always have delete access
    if (userRole === ROLES.DEVELOPER || userRole === ROLES.ADMIN) return true;
    
    // Check custom permissions for CAN_DELETE
    return customPermissions?.includes(PERMISSIONS.CAN_DELETE) || false;
  }, [userRole, customPermissions]);

  /**
   * Check if current user can manage another user
   */
  const canManageUser = useCallback((targetEmail, targetRole) => {
    // Cannot manage developer account
    if (isDeveloperEmail(targetEmail)) return false;
    
    // Only developer/admin can manage users
    if (!isAdminOrHigher()) return false;
    
    // Can only modify roles below your own
    return canModifyRole(userRole, targetRole);
  }, [userRole, isAdminOrHigher]);

  // ============================================
  // IMPERSONATION FUNCTIONS (View as User)
  // ============================================

  /**
   * Start impersonating a user - view the app as that user sees it
   * Only available for Developer and Admin roles
   */
  const startImpersonation = useCallback((targetUser) => {
    // Only developer/admin can impersonate (check original role, not current)
    const actualRole = originalUserData?.role || userRole;
    if (actualRole !== ROLES.DEVELOPER && actualRole !== ROLES.ADMIN) {
      logger.warn('Only Developer/Admin can impersonate users');
      return false;
    }

    // Cannot impersonate developer
    if (isDeveloperEmail(targetUser.email)) {
      logger.warn('Cannot impersonate developer account');
      return false;
    }

    // Store original user data before switching
    if (!isImpersonating) {
      setOriginalUserData({
        role: userRole,
        permissions: userPermissions,
        campaigns: assignedCampaigns,
        pages: allowedPages,
        customPermissions: customPermissions
      });
    }

    // Switch to target user's view
    setImpersonatedUser(targetUser);
    setUserRole(targetUser.role);
    setUserPermissions(targetUser.custom_permissions || DEFAULT_ROLE_PERMISSIONS[targetUser.role] || []);
    setAssignedCampaigns(targetUser.assigned_campaigns || []);
    setAllowedPages(targetUser.allowed_pages || DEFAULT_ROLE_PAGES[targetUser.role] || []);
    setCustomPermissions(targetUser.custom_permissions);
    setIsImpersonating(true);

    logger.log(`🔄 Now viewing as: ${targetUser.email} (${targetUser.role})`);
    return true;
  }, [userRole, userPermissions, assignedCampaigns, allowedPages, customPermissions, isImpersonating, originalUserData]);

  /**
   * Stop impersonation and restore original admin/developer access
   */
  const stopImpersonation = useCallback(() => {
    if (!isImpersonating || !originalUserData) {
      return false;
    }

    // Restore original user data
    setUserRole(originalUserData.role);
    setUserPermissions(originalUserData.permissions);
    setAssignedCampaigns(originalUserData.campaigns);
    setAllowedPages(originalUserData.pages);
    setCustomPermissions(originalUserData.customPermissions);

    // Clear impersonation state
    setImpersonatedUser(null);
    setOriginalUserData(null);
    setIsImpersonating(false);

    logger.log('🔄 Exited user view - restored original access');
    return true;
  }, [isImpersonating, originalUserData]);

  /**
   * Check if current user can impersonate others
   * Only Developer and Admin can use this feature
   */
  const canImpersonate = useCallback(() => {
    // If already impersonating, check original role
    const actualRole = originalUserData?.role || userRole;
    return actualRole === ROLES.DEVELOPER || actualRole === ROLES.ADMIN;
  }, [userRole, originalUserData]);

  // ============================================
  // ADMIN FUNCTIONS
  // ============================================

  /**
   * Fetch all users (admin only)
   */
  const fetchAllUsers = useCallback(async () => {
    if (!isAdminOrHigher()) return [];

    try {
      const { data, error } = await supabase
        .from(USERS_ROLES_TABLE)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Always include developer in the list (even if not in DB)
      const users = data || [];
      const hasDeveloper = users.some(u => isDeveloperEmail(u.email));
      
      if (!hasDeveloper) {
        users.unshift({
          email: DEVELOPER_EMAIL,
          role: ROLES.DEVELOPER,
          assigned_campaigns: [],
          is_permanent: true,
          created_at: new Date().toISOString()
        });
      }

      setAllUsers(users);
      return users;
    } catch (error) {
      logger.error('Error fetching all users:', error);
      return [];
    }
  }, [isAdminOrHigher]);

  /**
   * Update user role (admin only)
   */
  const updateUserRole = useCallback(async (targetEmail, newRole) => {
    // Security checks
    if (isDeveloperEmail(targetEmail)) {
      throw new Error('Cannot modify developer account');
    }
    
    if (!isAdminOrHigher()) {
      throw new Error('Insufficient permissions');
    }
    
    // Cannot assign role equal to or higher than your own (except developer can do anything)
    if (userRole !== ROLES.DEVELOPER && ROLE_HIERARCHY[newRole] >= ROLE_HIERARCHY[userRole]) {
      throw new Error('Cannot assign role equal to or higher than your own');
    }

    try {
      const { data: existing } = await supabase
        .from(USERS_ROLES_TABLE)
        .select('*')
        .eq('email', targetEmail.toLowerCase())
        .single();

      const updateData = {
        email: targetEmail.toLowerCase(),
        role: newRole,
        custom_permissions: DEFAULT_ROLE_PERMISSIONS[newRole],
        updated_at: new Date().toISOString(),
        updated_by: currentUser.email
      };

      let result;
      if (existing) {
        result = await supabase
          .from(USERS_ROLES_TABLE)
          .update(updateData)
          .eq('email', targetEmail.toLowerCase())
          .select();
      } else {
        result = await supabase
          .from(USERS_ROLES_TABLE)
          .insert({
            ...updateData,
            assigned_campaigns: [],
            created_at: new Date().toISOString()
          })
          .select();
      }

      if (result.error) throw result.error;

      await fetchAllUsers();
      return result.data;
    } catch (error) {
      logger.error('Error updating user role:', error);
      throw error;
    }
  }, [userRole, currentUser?.email, isAdminOrHigher, fetchAllUsers]);

  /**
   * Update user's assigned campaigns (admin only)
   */
  const updateUserCampaigns = useCallback(async (targetEmail, campaigns) => {
    if (isDeveloperEmail(targetEmail)) {
      throw new Error('Cannot modify developer account');
    }
    
    if (!isAdminOrHigher()) {
      throw new Error('Insufficient permissions');
    }

    try {
      const { data: existing } = await supabase
        .from(USERS_ROLES_TABLE)
        .select('*')
        .eq('email', targetEmail.toLowerCase())
        .single();

      const updateData = {
        assigned_campaigns: campaigns,
        updated_at: new Date().toISOString(),
        updated_by: currentUser.email
      };

      let result;
      if (existing) {
        result = await supabase
          .from(USERS_ROLES_TABLE)
          .update(updateData)
          .eq('email', targetEmail.toLowerCase())
          .select();
      } else {
        result = await supabase
          .from(USERS_ROLES_TABLE)
          .insert({
            email: targetEmail.toLowerCase(),
            role: ROLES.USER,
            custom_permissions: DEFAULT_ROLE_PERMISSIONS[ROLES.USER],
            assigned_campaigns: campaigns,
            created_at: new Date().toISOString(),
            updated_by: currentUser.email
          })
          .select();
      }

      if (result.error) throw result.error;

      await fetchAllUsers();
      return result.data;
    } catch (error) {
      logger.error('Error updating user campaigns:', error);
      throw error;
    }
  }, [currentUser?.email, isAdminOrHigher, fetchAllUsers]);

  /**
   * Update user's custom permissions (admin only)
   */
  const updateUserPermissions = useCallback(async (targetEmail, permissions) => {
    if (isDeveloperEmail(targetEmail)) {
      throw new Error('Cannot modify developer account');
    }
    
    if (!isAdminOrHigher()) {
      throw new Error('Insufficient permissions');
    }

    try {
      const { error } = await supabase
        .from(USERS_ROLES_TABLE)
        .update({
          custom_permissions: permissions,
          updated_at: new Date().toISOString(),
          updated_by: currentUser.email
        })
        .eq('email', targetEmail.toLowerCase());

      if (error) throw error;

      await fetchAllUsers();
    } catch (error) {
      logger.error('Error updating user permissions:', error);
      throw error;
    }
  }, [currentUser?.email, isAdminOrHigher, fetchAllUsers]);

  /**
   * Update user's allowed pages (admin only)
   */
  const updateUserPages = useCallback(async (targetEmail, pages) => {
    if (isDeveloperEmail(targetEmail)) {
      throw new Error('Cannot modify developer account');
    }
    
    if (!isAdminOrHigher()) {
      throw new Error('Insufficient permissions');
    }

    try {
      const { error } = await supabase
        .from(USERS_ROLES_TABLE)
        .update({
          allowed_pages: pages,
          updated_at: new Date().toISOString(),
          updated_by: currentUser.email
        })
        .eq('email', targetEmail.toLowerCase());

      if (error) throw error;

      await fetchAllUsers();
    } catch (error) {
      logger.error('Error updating user pages:', error);
      throw error;
    }
  }, [currentUser?.email, isAdminOrHigher, fetchAllUsers]);

  /**
   * Add new user (admin only)
   */
  const addUser = useCallback(async (email, role, campaigns = [], pages = null) => {
    if (isDeveloperEmail(email)) {
      throw new Error('Cannot add developer email');
    }
    
    if (!isAdminOrHigher()) {
      throw new Error('Insufficient permissions');
    }

    try {
      // Use provided pages or default for role
      const allowedPagesForUser = pages && pages.length > 0 ? pages : DEFAULT_ROLE_PAGES[role] || [];
      
      const { error } = await supabase
        .from(USERS_ROLES_TABLE)
        .insert({
          email: email.toLowerCase(),
          role: role,
          custom_permissions: DEFAULT_ROLE_PERMISSIONS[role],
          assigned_campaigns: campaigns,
          allowed_pages: allowedPagesForUser,
          created_at: new Date().toISOString(),
          updated_by: currentUser.email
        });

      if (error) throw error;

      await fetchAllUsers();
    } catch (error) {
      logger.error('Error adding user:', error);
      throw error;
    }
  }, [currentUser?.email, isAdminOrHigher, fetchAllUsers]);

  /**
   * Remove user (admin only - cannot remove developer)
   */
  const removeUser = useCallback(async (targetEmail) => {
    if (isDeveloperEmail(targetEmail)) {
      throw new Error('Cannot remove developer account');
    }
    
    if (!isAdminOrHigher()) {
      throw new Error('Insufficient permissions');
    }

    try {
      const { error } = await supabase
        .from(USERS_ROLES_TABLE)
        .delete()
        .eq('email', targetEmail.toLowerCase());

      if (error) throw error;

      await fetchAllUsers();
    } catch (error) {
      logger.error('Error removing user:', error);
      throw error;
    }
  }, [isAdminOrHigher, fetchAllUsers]);

  const value = {
    // User state
    userRole,
    userPermissions,
    assignedCampaigns,
    allowedPages,
    customPermissions,
    loading,
    
    // Access control
    accessDenied,
    accessDeniedReason,
    
    // All users (for admin panel)
    allUsers,
    
    // Permission checks
    hasPermission,
    canAccessPage,
    canAccessCampaign,
    filterByCampaign,
    isDeveloper,
    isAdminOrHigher,
    canManageUser,
    canDelete,
    
    // Impersonation (View as User)
    isImpersonating,
    impersonatedUser,
    startImpersonation,
    stopImpersonation,
    canImpersonate,
    
    // Admin actions
    fetchAllUsers,
    updateUserRole,
    updateUserCampaigns,
    updateUserPermissions,
    updateUserPages,
    addUser,
    removeUser,
    
    // Refresh
    refreshUserRole: fetchUserRole,
    
    // Constants (exposed for components)
    ROLES,
    PERMISSIONS,
    DEVELOPER_EMAIL,
    ALL_PAGES,
    DEFAULT_ROLE_PAGES
  };

  return (
    <RBACContext.Provider value={value}>
      {children}
    </RBACContext.Provider>
  );
}
